import { Container, Graphics } from 'pixi.js';
import {
  CAT_BASE_SPEED,
  CAT_TYGYDYK_SPEED,
  TYGYDYK_DRAIN_RATE,
  TYGYDYK_MAX,
  TYGYDYK_RECOVERY_RATE
} from '../constants';

export class Cat {
  public container: Container;
  public x: number = 0;
  public y: number = 0;
  public vx: number = 0;
  public vy: number = 0;
  public angle: number = 0;

  public isActing: boolean = false;
  public actionType: string | null = null;
  public isTygydyk: boolean = false; // Sprint mode
  public tygydyk: number = TYGYDYK_MAX; // Rename stamina -> tygydyk
  public readonly radius: number = 22;

  private bodyContainer: Container;
  private tail: Graphics;
  private leftPaw: Graphics;
  private rightPaw: Graphics;
  private head: Graphics;
  private leftEye: Graphics;
  private rightEye: Graphics;
  private speedAura: Graphics;

  private animTimer: number = 0;
  private blinkTimer: number = 0;

  constructor() {
    this.container = new Container();

    this.bodyContainer = new Container();
    this.container.addChild(this.bodyContainer);

    // Speed Trail / Zoomies Aura
    this.speedAura = new Graphics();
    this.bodyContainer.addChild(this.speedAura);

    // 1. Soft Shadow on floor
    const shadow = new Graphics();
    shadow.ellipse(0, 4, 24, 18);
    shadow.fill({ color: 0x000000, alpha: 0.25 });
    this.bodyContainer.addChild(shadow);

    // 2. Tail with dark ticking tip
    this.tail = new Graphics();
    this.drawTail(0);
    this.bodyContainer.addChild(this.tail);

    // 3. Paws (British round paws)
    this.leftPaw = new Graphics();
    this.rightPaw = new Graphics();
    this.drawPaws(0);
    this.bodyContainer.addChild(this.leftPaw);
    this.bodyContainer.addChild(this.rightPaw);

    // 4. Main Body - Golden Chinchilla (warm apricot golden coat)
    const body = new Graphics();
    body.ellipse(0, 0, 20, 24);
    body.fill({ color: 0xe6a15c });
    body.stroke({ width: 2, color: 0x4a2810 });

    body.ellipse(0, 2, 16, 18);
    body.fill({ color: 0xf5b041 });

    body.ellipse(0, 6, 8, 12);
    body.fill({ color: 0xc0392b, alpha: 0.15 });

    this.bodyContainer.addChild(body);

    // 5. Head Container (Round British Face)
    this.head = new Graphics();
    this.drawHead();
    this.bodyContainer.addChild(this.head);

    // 6. Emerald Green Eyes with Dark Eyeliner
    this.leftEye = new Graphics();
    this.rightEye = new Graphics();
    this.drawEyes(false, false);
    this.bodyContainer.addChild(this.leftEye);
    this.bodyContainer.addChild(this.rightEye);
  }

  private drawHead() {
    this.head.clear();

    // Left ear
    this.head.poly([
      { x: -14, y: -16 },
      { x: -18, y: -30 },
      { x: -6, y: -22 }
    ]);
    this.head.fill({ color: 0x5d4037 });
    this.head.poly([
      { x: -12, y: -17 },
      { x: -16, y: -27 },
      { x: -7, y: -21 }
    ]);
    this.head.fill({ color: 0xffb8b8 });

    // Right ear
    this.head.poly([
      { x: 14, y: -16 },
      { x: 18, y: -30 },
      { x: 6, y: -22 }
    ]);
    this.head.fill({ color: 0x5d4037 });
    this.head.poly([
      { x: 12, y: -17 },
      { x: 16, y: -27 },
      { x: 7, y: -21 }
    ]);
    this.head.fill({ color: 0xffb8b8 });

    // Round British head
    this.head.circle(0, -14, 16);
    this.head.fill({ color: 0xe6a15c });
    this.head.stroke({ width: 2, color: 0x4a2810 });

    // Chubby cheeks
    this.head.circle(-7, -10, 8);
    this.head.fill({ color: 0xfed39f });
    this.head.circle(7, -10, 8);
    this.head.fill({ color: 0xfed39f });

    // Pink Nose
    this.head.poly([
      { x: -3, y: -12 },
      { x: 3, y: -12 },
      { x: 0, y: -9 }
    ]);
    this.head.fill({ color: 0xff7675 });
    this.head.stroke({ width: 1, color: 0x2d3436 });

    // Whiskers
    this.head.moveTo(-8, -10);
    this.head.lineTo(-24, -13);
    this.head.moveTo(-8, -8);
    this.head.lineTo(-24, -7);

    this.head.moveTo(8, -10);
    this.head.lineTo(24, -13);
    this.head.moveTo(8, -8);
    this.head.lineTo(24, -7);
    this.head.stroke({ width: 1.5, color: 0x2c1810 });
  }

  private drawEyes(closed: boolean, crazyZoomies: boolean) {
    this.leftEye.clear();
    this.rightEye.clear();

    if (closed) {
      this.leftEye.moveTo(-10, -16);
      this.leftEye.lineTo(-4, -16);
      this.leftEye.stroke({ width: 2.5, color: 0x2c1810 });

      this.rightEye.moveTo(4, -16);
      this.rightEye.lineTo(10, -16);
      this.rightEye.stroke({ width: 2.5, color: 0x2c1810 });
    } else if (crazyZoomies) {
      this.leftEye.circle(-7, -16, 5.5);
      this.leftEye.fill({ color: 0x000000 });
      this.leftEye.circle(-6, -18, 2);
      this.leftEye.fill({ color: 0xffffff });

      this.rightEye.circle(7, -16, 5.5);
      this.rightEye.fill({ color: 0x000000 });
      this.rightEye.circle(8, -18, 2);
      this.rightEye.fill({ color: 0xffffff });
    } else {
      this.leftEye.circle(-7, -16, 4.5);
      this.leftEye.fill({ color: 0x1e272e });
      this.leftEye.circle(-7, -16, 3.8);
      this.leftEye.fill({ color: 0x10ac84 });
      this.leftEye.ellipse(-7, -16, 1.8, 3.2);
      this.leftEye.fill({ color: 0x000000 });
      this.leftEye.circle(-6, -17, 1.2);
      this.leftEye.fill({ color: 0xffffff });

      this.rightEye.circle(7, -16, 4.5);
      this.rightEye.fill({ color: 0x1e272e });
      this.rightEye.circle(7, -16, 3.8);
      this.rightEye.fill({ color: 0x10ac84 });
      this.rightEye.ellipse(7, -16, 1.8, 3.2);
      this.rightEye.fill({ color: 0x000000 });
      this.rightEye.circle(8, -17, 1.2);
      this.rightEye.fill({ color: 0xffffff });
    }
  }

  private drawTail(swish: number) {
    this.tail.clear();
    this.tail.moveTo(0, 18);
    const endX = Math.sin(swish) * 14;
    const endY = 38 + Math.cos(swish) * 4;
    this.tail.quadraticCurveTo(Math.sin(swish) * 6, 28, endX, endY);
    this.tail.stroke({ width: 8, color: 0xe6a15c, cap: 'round' });

    this.tail.circle(endX, endY, 4.5);
    this.tail.fill({ color: 0x3e2723 });
  }

  private drawPaws(step: number) {
    this.leftPaw.clear();
    this.rightPaw.clear();

    const leftY = -18 + Math.sin(step) * 5;
    const rightY = -18 - Math.sin(step) * 5;

    this.leftPaw.ellipse(-14, leftY, 5, 7);
    this.leftPaw.fill({ color: 0xfed39f });
    this.leftPaw.stroke({ width: 1.5, color: 0x4a2810 });

    this.rightPaw.ellipse(14, rightY, 5, 7);
    this.rightPaw.fill({ color: 0xfed39f });
    this.rightPaw.stroke({ width: 1.5, color: 0x4a2810 });
  }

  public update(delta: number, wantsSprint: boolean) {
    this.animTimer += delta * 0.15;

    const isMoving = Math.abs(this.vx) > 0.1 || Math.abs(this.vy) > 0.1;

    // Handle Tygydyk meter
    if (wantsSprint && isMoving && this.tygydyk > 5) {
      this.isTygydyk = true;
      this.tygydyk = Math.max(0, this.tygydyk - (TYGYDYK_DRAIN_RATE * delta) / 60);
    } else {
      this.isTygydyk = false;
      this.tygydyk = Math.min(TYGYDYK_MAX, this.tygydyk + (TYGYDYK_RECOVERY_RATE * delta) / 60);
    }

    // Speed aura during Tygydyk
    this.speedAura.clear();
    if (this.isTygydyk) {
      this.speedAura.ellipse(0, 0, 26, 32);
      this.speedAura.fill({ color: 0xf1c40f, alpha: 0.35 + Math.sin(this.animTimer * 5) * 0.15 });
      this.drawEyes(false, true);
    }

    if (isMoving && !this.isActing) {
      const targetAngle = Math.atan2(this.vy, this.vx) + Math.PI / 2;
      let diff = targetAngle - this.angle;
      while (diff < -Math.PI) diff += Math.PI * 2;
      while (diff > Math.PI) diff -= Math.PI * 2;
      this.angle += diff * 0.28;

      const pawAnimSpeed = this.isTygydyk ? 4.5 : 2.0;
      this.drawPaws(this.animTimer * pawAnimSpeed);
      this.drawTail(this.animTimer * 1.5);
      this.bodyContainer.scale.set(1.0 + Math.sin(this.animTimer * 2) * 0.05, 1.0 - Math.sin(this.animTimer * 2) * 0.05);
    } else if (this.isActing) {
      if (this.actionType === 'hairball_spot') {
        const cough = Math.sin(this.animTimer * 6);
        this.bodyContainer.scale.set(1.0 + cough * 0.15, 1.0 - cough * 0.2);
        this.drawPaws(0);
        this.drawTail(0);
      } else {
        this.drawPaws(this.animTimer * 6);
        this.drawTail(this.animTimer * 4);
        this.bodyContainer.scale.set(1.0, 1.0);
      }
    } else {
      this.drawPaws(0);
      this.drawTail(this.animTimer * 0.6);
      this.bodyContainer.scale.set(1.0 + Math.sin(this.animTimer * 0.8) * 0.02, 1.0 + Math.sin(this.animTimer * 0.8) * 0.02);
    }

    this.bodyContainer.rotation = this.angle;

    if (!this.isTygydyk) {
      this.blinkTimer += delta;
      if (this.blinkTimer > 160) {
        this.drawEyes(true, false);
        if (this.blinkTimer > 175) {
          this.drawEyes(false, false);
          this.blinkTimer = 0;
        }
      }
    }

    this.container.position.set(this.x, this.y);
  }

  public getSpeed(): number {
    return this.isTygydyk ? CAT_TYGYDYK_SPEED : CAT_BASE_SPEED;
  }

  public setPosition(x: number, y: number) {
    this.x = x;
    this.y = y;
    this.container.position.set(x, y);
  }
}
